'use strict'

export class Float {
	#value;
	constructor(x) {
		const view = new DataView(new ArrayBuffer(4));
		view.setFloat32(0, x);
		this.#value = view.getFloat32(0);
	}
	get value() {
		return this.#value;
	}

	get plusUlp() {
		return new Float(this.value + MathUtils.ulp32(this.value));
	}

	get minusUlp() {
		return new Float(this.value - MathUtils.ulp32(-this.value));
	}

	static maxValue(){
		// 0 11111110 11111111111111111111111
		const view = new DataView(new ArrayBuffer(4));
		view.setInt32(0, 0x7f7fffff);
		return new Float(view.getFloat32(0));
	}
}

export default class MathUtils {
	/**
	 * @param {number[]} values
	 * @return {number}
	 */
	static mean(values) {
		if (!Array.isArray(values) || values.length < 1)
			throw new Error("Dataset is not array or is empty")
		let sum = 0;
		for (const value of values)
			sum += value;
		return sum / values.length;
	}

	/**
	 * add link
	 * @param {number[]} values
	 * @return {{variance: number, mean: number}}
	 */
	static variance(values) {
		const mean = MathUtils.mean(values);
		let sum = 0;
		for (const value of values)
			sum += Math.pow(value - mean, 2)
		return {mean: mean, variance: sum / (values.length - 1)}
	}

	/**
	 * @param {number[]} values
	 * @return {{sd: number, mean: number}}
	 */
	static standardDeviation(values) {
		const {mean, variance} = MathUtils.variance(values);
		return {mean: mean, sd: Math.sqrt(variance)}
	}

	/**
	 * Returns the size of an ulp of the argument.  An ulp, unit in
	 * the last place, of a float value is the positive
	 * distance between this floating-point value and the float value
	 * next larger in magnitude.  Note that for non-NaN
	 * <i>x</i>, <code>ulp(-<i>x</i>) == ulp(<i>x</i>)</code>.
	 *
	 * <p>Special Cases:
	 * <ul>
	 * <li> If the argument is NaN, then the result is NaN.
	 * <li> If the argument is positive or negative infinity, then the
	 * result is positive infinity.
	 * </ul>
	 *
	 * @param x the floating-point value whose ulp is to be returned
	 * @return {number} the size of an ulp of the argument
	 */
	static ulp32(x) {
		if (isNaN(x))
			return NaN;
		if (!isFinite(x))
			return Infinity;
		const float = new Float(x).value;
		const multiplier = float < 0
			? 1 - MathUtils.FLOAT32_EPSILON
			: 1 + MathUtils.FLOAT32_EPSILON;
		return (float * multiplier) - float;
	}
	static FLOAT32_EPSILON = new Float(Math.pow(2, -23)).value;
}

